import { Modal, Notification, STATUS_TYPES, Step, USER_PERMISSIONS } from "dyl-components";
import { useContext, useState } from "react";
import { useController, useFieldArray, useFormContext } from "react-hook-form";
import { Button, Form, Grid, Icon, Popup, Segment } from "semantic-ui-react";
import OrderSummary from "./OrderSummary";
import { useDispatch, useSelector } from "react-redux";
import { ACTIONS, CURRENT_STEPS, QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";

import orderActions from "actions/order";
import { formatOrder } from "shared/schemas/cart/cartSchema";
import FullScreenModalPopUp from "shared/FullScreenModalPopUp";
import paymentActions from "actions/payment";
import { DYL_ACCOUNT_ID } from "shared/modals/QuoteBuilderModal/helper";
import RestrictedView from "shared/RestrictedView";

const STEPS = [
    {
        icon: <Icon className="fas fa-box-dollar" size="large" />,
        title: "Order",
        active: true,
    },
    {
        icon: <Icon className="fas fa-file-invoice-dollar" size="large" />,
        title: "Checkout",
    },
];

const Order = ({ account_id }) => {
    const {
        formState: { isValid, isDirty },
        getFieldState,
        handleSubmit,
        control,
        watch,
    } = useFormContext();

    const order = useSelector((state) => state.order.order);
    const customer_order = order?.customer_account_discounts;
    const fund_type = order?.account?.fund_type;
    const isCanceled = order?.account?.customer_status === "canceled";
    const trial_period = order?.trial_period;
    const { plan_id } = useSelector((state) => {
        const { is_current_price_schedule, plan_id } = state.subscription.term || { term: null };
        if (is_current_price_schedule || !isCanceled) {
            return { plan_id };
        }
        return {};
    });

    const dispatch = useDispatch();

    const { field: termField } = useController({
        name: "term",
        control,
    });

    const { onCheckout, onOpenAddProducts, onViewOrder, quoteBuilderConfig, onEditOrder, onRefreshAccountInformation, onCloseModal } =
        useContext(QuoteBuilderContext);

    const { id: order_id, currentStep, action } = quoteBuilderConfig;

    const updateOrder = async (data) => {
        if (data.shipping_address?.location?.address?.zip) {
            await dispatch(orderActions.updateAddresses(account_id, {
                shipping: {
                    location: data.shipping_address,
                    ship_to: data.shipping_address.ship_to
                }
            }, null, `${order_id}/location`));
        }
        return dispatch(
            orderActions.update(
                account_id,
                {
                    order_items: formatOrder(data.cart).map(item => ({
                        ...item,
                        addons: (item?.addons || []).map(addon => ({
                            id: addon,
                            cart_item_id: item.product.cart_item_id
                        }))
                    })),
                    order_name: data.order_name,
                    order_status: data.order_status
                },
                null,
                order_id
            )
        ).then(() => dispatch(orderActions.readOne(account_id, null, order_id)));
    }

    const onUpdateOrder = async (data) => {
        const action = data.order_status === 'deleted' ? 'delete' : 'update';
        try {
            await updateOrder(data);
            if (action !== 'delete') {
                onEditOrder(order_id, account_id);
            } else {
                onCloseModal();
            }
            Notification.alert(`Successfully ${action}d order!`, STATUS_TYPES.SUCCESS);
            onRefreshAccountInformation();
        } catch (e) {
            console.log(e);
            Notification.alert(`Failed to ${action} order`, STATUS_TYPES.ERROR);
        }
    }
    console.log("|+|current", currentStep)
    const isDYLAccount = Number(account_id) === DYL_ACCOUNT_ID;
    const isDyl = useSelector(state => state.auth.dyl_employee);
    const isReactivating = isCanceled || order?.account?.customer_status === "paused";

    const onProceedToCheckout = async (data) => {
        try {
            await updateOrder({
                ...data,
                order_status: "open"
            });
            onRefreshAccountInformation();
            if (isDyl && !isDYLAccount) {
                dispatch(paymentActions.getPaymentMethods(account_id));
            }
            if (!isDYLAccount) {
                dispatch(paymentActions.getProfiles(account_id, { price_schedule: data.cart.find(item => item.pricing_schedule)?.pricing_schedule })).then((response) => {
                    const termValue = (() => {
                        if (fund_type === "demo" || action === ACTIONS.DEMO) {
                            return "demo";
                        }
                        if (fund_type === "trial" && !isReactivating) {
                            return `trial/${trial_period}`;
                        }
                        if (!isDyl) {
                            return response.term ? `net/${response.term}` : "default";
                        }
                        return null;
                    })();
                    termField.onChange({ target: { name: termField.name, value: termValue } });
                });
            }
            onCheckout();
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to proceed to checkout", STATUS_TYPES.ERROR);
        }
    };

    const { field: orderStatusField } = useController({
        name: "order_status",
        control,
    });

    const isDeletingOrder = orderStatusField.value === "deleted";
    const isDraft = orderStatusField.value === "draft";
    const isAllowedToEdit =
        orderStatusField.value === "open" ||
        isDraft ||
        (
            (isDeletingOrder && order?.order_status !== "deleted") || !Boolean(order?.order_status)
        );

    const { replace } = useFieldArray({
        control,
        name: "cart",
        keyName: "key",
    });

    const currentCartValue = watch('cart');

    const onDuplicateOrder = async (data) => {
        try {
            const order_id = await dispatch(
                orderActions.create(
                    {
                        items: formatOrder(data.cart),
                        name: `Duplicate of ${data.order_name || "Order"}`,
                        billing: data.billing_address
                    },
                    {
                        account_id,
                    }
                )
            );
            Notification.alert(
                "Successfully duplicated order",
                STATUS_TYPES.SUCCESS
            );
            onViewOrder(order_id, account_id);
            onRefreshAccountInformation();
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to duplicate order", STATUS_TYPES.ERROR);
        }
    };

    const isSaving = useSelector(
        (state) => state.order.isCreating || state.order.orderBeingUpdated
    );

    const shouldConfirmCartChanges = (getFieldState('cart')?.isDirty && currentStep === CURRENT_STEPS.EDIT_ORDER) || isDeletingOrder;

    const [actionBeingConfirmed, setActionBeingConfirmed] = useState(null);

    const watchedCart = watch('cart');

    const shouldHaveReactivation = isDyl && isCanceled && !watchedCart.some(item => !item.removed && item.is_reactivation);
    const shouldNotHaveReactivation = isDyl && !isCanceled && watchedCart.some(item => !item.removed && item.is_reactivation);
    const hasMismatchingPlans = isDyl && plan_id && !isCanceled && watchedCart.some(item => !item.removed && item.is_plan && item.id !== plan_id);
    const isPlanDeleted = isDyl && !plan_id && watchedCart.some(item => item.removed && item.is_plan);
    const isCalculatingTaxes = useSelector(state => state.order.isCalculatingTaxes);
    const [wereTaxesCalculated, setWereTaxesCalculated] = useState(true);
    const onPromptTaxCalculation = () => {
        if (isDyl) {
            setWereTaxesCalculated(false);
        }
    };
    const finishTaxCalculation = () => {
        setWereTaxesCalculated(true);
    }


    const handleActionConfirmation = (action) => {
        const diffrentDiscounts = watchedCart.filter((cart) => cart.removed !== true).some(item => {
            return customer_order?.find(discount => discount.product_id === item.id)?.discount !== Number(item.discount);
        });
        if (diffrentDiscounts && customer_order) {
            setActionBeingConfirmed(`discount_${action}`);
        } else if (shouldConfirmCartChanges) {
            setActionBeingConfirmed((() => {
                if (action === "save") {
                    return !isDeletingOrder ? "update" : "delete";
                }
                return "checkout";
            })())
        } else if (action === 'save') {
            handleSubmit(onUpdateOrder)();
        } else {
            handleSubmit(onProceedToCheckout)();
        }
    };

    return (
        <>
            <FullScreenModalPopUp
                header={'Are you sure?'}
                subheader={(() => {
                    if (!actionBeingConfirmed?.includes("discount")) {
                        return actionBeingConfirmed !== 'delete' ? 'Product changes will be applied.' : 'This order will be deleted.'
                    }
                    return "Discount % do not match"
                })()}
                isOpen={actionBeingConfirmed}
                onConfirm={() => {
                    if (!actionBeingConfirmed?.includes("discount")) {
                        handleSubmit(actionBeingConfirmed === 'update' || actionBeingConfirmed === 'delete' ? onUpdateOrder : onProceedToCheckout)();
                    } else {
                        const pendingAction = actionBeingConfirmed?.split("_")[1];
                        if (pendingAction === 'save') {
                            handleSubmit(onUpdateOrder)();
                        }
                        if (pendingAction === 'next') {
                            handleSubmit(onProceedToCheckout)();
                        }
                    }
                    setActionBeingConfirmed(null)
                }}
                onFormClose={() => setActionBeingConfirmed(null)}
                closeIcon={false}
            />
            <Modal.Content scrolling>
                <Form loading={isSaving} size="small" noValidate>
                    <Segment size="tiny" basic>
                        <Grid>
                            {isAllowedToEdit && (
                                <Grid.Row>
                                    <Grid.Column>
                                        <Step.Group horizontal>
                                            {STEPS.map(({ icon, ...step }) => (
                                                <Step {...step} key={step.name}>
                                                    {icon}
                                                </Step>
                                            ))}
                                        </Step.Group>
                                    </Grid.Column>
                                </Grid.Row>
                            )}
                            <Grid.Row>
                                <Grid.Column>
                                    <OrderSummary
                                        quote={{
                                            quote_summary: order.order_items?.map(item => ({
                                                ...item,
                                                unmodifiable: isDyl && isCanceled && item.dyl_product_name === "account_reactivation",
                                                needToRemoveMessage: (() => {
                                                    if (!isDyl) {
                                                        return null;
                                                    }
                                                    if (!isCanceled && item.dyl_product_name === "account_reactivation") {
                                                        return "This product can only be added to a canceled account.";
                                                    }
                                                    if (plan_id && item.is_plan && item.product_id !== plan_id) {
                                                        return "This does not match the current plan.";
                                                    }
                                                    return null;
                                                })()
                                            })) || [],
                                        }}
                                        account_id={account_id}
                                        disabled={!isAllowedToEdit}
                                        onPromptTaxCalculation={onPromptTaxCalculation}
                                        wereTaxesCalculated={wereTaxesCalculated}
                                        finishTaxCalculation={finishTaxCalculation}
                                        canceled_order={order?.canceled_order}
                                        shouldNotDisplayPriceChanges={order?.order_status === 'completed' || order?.order_status === 'deleted'}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Segment>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                {isAllowedToEdit ? (
                    <>
                        {shouldHaveReactivation && (
                            <Popup
                                trigger={<Icon size='large' className='fa-solid fa-triangle-exclamation WarningRed' />}
                                content={(
                                    <div className="MinimumErrors" style={{ width: '300px' }}>
                                        Please add the reactivation product to proceed.
                                    </div>
                                )}
                                position="left center"
                                open
                            />
                        )}
                        <Button
                            basic
                            onClick={() => {
                                replace(currentCartValue.filter((item) => {
                                    const isNotReactivationItem = !(shouldNotHaveReactivation && item.is_reactivation);
                                    const isNotMismatchingPlan = !isDyl || !plan_id || !item.is_plan || (item.is_plan && item.id === plan_id);
                                    return !Boolean(item.removed) && isNotReactivationItem && isNotMismatchingPlan;
                                }))
                                onOpenAddProducts({
                                    order_id,
                                    isUpsell: true,
                                });
                            }}
                            type="button"
                            color="primary"
                            disabled={isSaving || isDeletingOrder || isCalculatingTaxes}
                        >
                            Back to Add Products
                        </Button>
                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ORDERS_EDIT} userRestrict>
                            <Button
                                disabled={(!isValid || !isDirty || isSaving || shouldNotHaveReactivation || hasMismatchingPlans || isPlanDeleted || isCalculatingTaxes) && !isDeletingOrder}
                                type="button"
                                color="primary"
                                basic
                                onClick={() => {handleActionConfirmation('save')}}
                                loading={isSaving}
                            >
                                Save
                            </Button>
                        </RestrictedView>
                        <RestrictedView type="disabled" userPermission={currentStep === CURRENT_STEPS.EDIT_ORDER ? USER_PERMISSIONS.ORDERS_EDIT : USER_PERMISSIONS.ORDERS_ADD} userRestrict>
                            <Button
                                disabled={!isValid || isSaving || isDeletingOrder || (shouldHaveReactivation) || shouldNotHaveReactivation || hasMismatchingPlans || isPlanDeleted || isCalculatingTaxes || !wereTaxesCalculated}
                                type="button"
                                color="primary"
                                onClick={() => {handleActionConfirmation('next')}}
                                loading={isSaving}
                            >
                                Next
                            </Button>
                        </RestrictedView>
                    </>
                ) : (
                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ORDERS_ADD} userRestrict>
                        <Button
                            disabled={!isValid || isSaving || order?.canceled_order}
                            type="button"
                            color="primary"
                            onClick={handleSubmit(onDuplicateOrder)}
                            loading={isSaving}
                        >
                            Create New Order
                        </Button>
                    </RestrictedView>
                )}
            </Modal.Actions>
        </>
    );
};

export default Order;
